import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ShieldAlert, Moon, ChevronDown, ChevronUp, Flame } from 'lucide-react'
import { useHealthDashboard } from '@/hooks/use-gamification'
import { StreakBadge } from './StreakBadge'

export function HealthDashboardCard() {
  const { t } = useTranslation()
  const { data, isLoading } = useHealthDashboard()
  const [expanded, setExpanded] = useState(false)

  if (isLoading || !data) {
    return <div className="bg-surface-container-low h-32 animate-pulse rounded-xl" />
  }

  const { summary, atRiskContacts, dormantContacts, topStreaks } = data

  if (summary.total === 0) return null

  return (
    <section className="space-y-3">
      <button onClick={() => setExpanded(!expanded)} className="flex w-full items-center gap-2">
        <ShieldAlert className="text-primary h-4 w-4" />
        <h2 className="font-heading text-base font-bold">
          {t('gamification.healthDashboard', 'Health Dashboard')}
        </h2>
        <span className="flex-1" />
        {expanded ? (
          <ChevronUp className="text-on-surface-variant h-4 w-4" />
        ) : (
          <ChevronDown className="text-on-surface-variant h-4 w-4" />
        )}
      </button>

      {/* Summary pills */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-chartreuse-soft flex flex-col items-center rounded-xl p-3">
          <span className="text-chartreuse-foreground dark:text-chartreuse text-lg font-bold">
            {summary.active}
          </span>
          <span className="text-on-surface-variant text-[10px]">
            {t('gamification.active', 'Active')}
          </span>
        </div>
        <div className="bg-coral-soft flex flex-col items-center rounded-xl p-3">
          <span className="text-coral text-lg font-bold">{summary.atRisk}</span>
          <span className="text-on-surface-variant text-[10px]">
            {t('gamification.atRisk', 'At Risk')}
          </span>
        </div>
        <div className="bg-lilac-soft flex flex-col items-center rounded-xl p-3">
          <span className="text-lilac-foreground dark:text-lilac text-lg font-bold">
            {summary.dormant}
          </span>
          <span className="text-on-surface-variant text-[10px]">
            {t('gamification.dormant', 'Dormant')}
          </span>
        </div>
      </div>

      {expanded && (
        <div className="space-y-4">
          {/* Top Streaks */}
          {topStreaks.length > 0 && (
            <div className="bg-surface-container-low rounded-xl p-4">
              <div className="mb-3 flex items-center gap-2">
                <Flame className="text-coral h-4 w-4" />
                <h3 className="text-sm font-semibold">
                  {t('gamification.topStreaks', 'Top Streaks')}
                </h3>
              </div>
              <div className="space-y-2">
                {topStreaks.map((contact) => (
                  <Link
                    key={contact.id}
                    to={`/contacts/${contact.id}`}
                    className="hover:bg-surface-container-high flex items-center gap-3 rounded-lg p-2 transition-colors"
                  >
                    {contact.avatarUrl ? (
                      <img
                        src={contact.avatarUrl}
                        alt={contact.name}
                        className="h-7 w-7 rounded-full object-cover"
                      />
                    ) : (
                      <div className="bg-primary/10 text-primary flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold">
                        {contact.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="min-w-0 flex-1 truncate text-sm">{contact.name}</span>
                    <StreakBadge streak={contact.currentStreak} size="md" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* At Risk Contacts */}
          {atRiskContacts.length > 0 && (
            <div className="bg-surface-container-low rounded-xl p-4">
              <div className="mb-3 flex items-center gap-2">
                <ShieldAlert className="text-coral h-4 w-4" />
                <h3 className="text-sm font-semibold">
                  {t('gamification.atRiskContacts', 'At Risk')}
                </h3>
              </div>
              <div className="space-y-2">
                {atRiskContacts.map((contact) => (
                  <Link
                    key={contact.id}
                    to={`/contacts/${contact.id}`}
                    className="hover:bg-surface-container-high flex items-center gap-3 rounded-lg p-2 transition-colors"
                  >
                    {contact.avatarUrl ? (
                      <img
                        src={contact.avatarUrl}
                        alt={contact.name}
                        className="h-7 w-7 rounded-full object-cover"
                      />
                    ) : (
                      <div className="bg-coral-soft text-coral flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold">
                        {contact.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="min-w-0 flex-1 truncate text-sm">{contact.name}</span>
                    {contact.daysSinceContact != null && (
                      <span className="text-on-surface-variant text-xs">
                        {t('gamification.daysAgo', '{{count}}d ago', {
                          count: contact.daysSinceContact,
                        })}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Dormant Contacts */}
          {dormantContacts.length > 0 && (
            <div className="bg-surface-container-low rounded-xl p-4">
              <div className="mb-3 flex items-center gap-2">
                <Moon className="text-lilac-foreground dark:text-lilac h-4 w-4" />
                <h3 className="text-sm font-semibold">
                  {t('gamification.dormantContacts', 'Dormant')}
                </h3>
              </div>
              <div className="space-y-2">
                {dormantContacts.map((contact) => (
                  <Link
                    key={contact.id}
                    to={`/contacts/${contact.id}`}
                    className="hover:bg-surface-container-high flex items-center gap-3 rounded-lg p-2 transition-colors"
                  >
                    {contact.avatarUrl ? (
                      <img
                        src={contact.avatarUrl}
                        alt={contact.name}
                        className="h-7 w-7 rounded-full object-cover"
                      />
                    ) : (
                      <div className="bg-lilac-soft text-lilac-foreground dark:text-lilac flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold">
                        {contact.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="min-w-0 flex-1 truncate text-sm">{contact.name}</span>
                    {contact.daysSinceContact != null && (
                      <span className="text-on-surface-variant text-xs">
                        {t('gamification.daysAgo', '{{count}}d ago', {
                          count: contact.daysSinceContact,
                        })}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
